import { useState, useRef, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import { Popper, ClickAwayListener, Paper, Stack, Typography, Box } from '@mui/material';
import { format } from 'date-fns';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import 'react-datepicker/dist/react-datepicker.css';
import { TextField } from '../new/inputs/TextField';

type DateRange = (Date | null)[];

interface DatePickerProps {
  value: DateRange;
  onChange: (param: any) => void;
}

const DateRangePicker = ({ value, onChange }: DatePickerProps) => {
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [tempDateRange, setTempDateRange] = useState<DateRange>(value);

  useEffect(() => {
    const waitForHeader = setTimeout(() => {
      const header = document.querySelector('.react-datepicker__header');
      if (header) {
        let existingMonthYear = document.querySelector('.custom-month-year');
        if (!existingMonthYear) {
          existingMonthYear = document.createElement('div');
          existingMonthYear.className = 'custom-month-year';
          header.appendChild(existingMonthYear);
        }
        existingMonthYear.textContent = format(currentMonth, 'MMMM yyyy');
      }
    }, 10);

    return () => clearTimeout(waitForHeader);
  }, [currentMonth]);

  const handleClear = () => {
    setTempDateRange([null, null]);
    onChange([null, null]);
  };

  const handleCancel = () => {
    setOpen(false);
    setTempDateRange(value);
  };

  const handleApply = () => {
    onChange(tempDateRange);
    setOpen(false);
  };

  return (
    <>
      <TextField
        id="dateRange"
        name="dateRange"
        label="Date Range"
        error=""
        inputRef={anchorRef}
        value={value[0] && value[1] ? `${value[0].toLocaleDateString()} - ${value[1].toLocaleDateString()}` : ''}
        onClick={() => {
          setTempDateRange(value);
          setOpen(true);
        }}
        placeholder="Choose"
        sx={{ cursor: 'pointer', backgroundColor: 'white', '.MuiInputBase-root': { height: '2.75rem' } }}
      />
      <Popper open={open} anchorEl={anchorRef.current} placement="bottom-start">
        <ClickAwayListener onClickAway={handleCancel}>
          <Paper elevation={3} sx={{ padding: 1, borderRadius: 2 }}>
            <DatePicker
              selected={tempDateRange[0]}
              onChange={(update: DateRange) => setTempDateRange(update)}
              startDate={tempDateRange[0]}
              endDate={tempDateRange[1]}
              selectsRange
              inline
              renderCustomHeader={({ date, decreaseMonth, increaseMonth }) => {
                setCurrentMonth(date);
                return (
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <ChevronLeftIcon sx={{ cursor: 'pointer' }} onClick={decreaseMonth} />
                      <ChevronRightIcon sx={{ cursor: 'pointer' }} onClick={increaseMonth} />
                    </Box>
                    <Box sx={{ marginLeft: '0.625rem' }}>
                      <Typography sx={{ fontSize: '0.8125rem' }}>Start - End Dates</Typography>
                      <Typography sx={{ fontSize: '1.125rem', fontWeight: '500' }}>
                        {tempDateRange[0] && format(tempDateRange[0], 'MMM d')}{' '}
                        {tempDateRange[1] && ` - ${format(tempDateRange[1], 'MMM d')}`}
                      </Typography>
                    </Box>
                    <hr />
                  </Box>
                );
              }}
            />
            <Stack direction="row" spacing={1} justifyContent="space-between" sx={{ paddingX: 1, paddingY: 2 }}>
              <Typography
                sx={{ color: '#436eb2', cursor: 'pointer', fontSize: '0.8125rem', fontWeight: 600 }}
                onClick={handleClear}
              >
                Clear
              </Typography>
              <Stack direction="row" gap={3}>
                <Typography
                  sx={{ color: '#436eb2', cursor: 'pointer', fontSize: '0.8125rem', fontWeight: 600 }}
                  onClick={handleCancel}
                >
                  Cancel
                </Typography>
                <Typography
                  sx={{ color: '#436eb2', cursor: 'pointer', fontSize: '0.8125rem', fontWeight: 600 }}
                  onClick={handleApply}
                >
                  OK
                </Typography>
              </Stack>
            </Stack>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
};

export default DateRangePicker;
